import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { ProductParamInput } from 'containers/ProductRegist/hooks/types';
import { MeData } from 'graphQL/Auth/types';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';
import { CREATE_PRODUCT, SAVE_PRODUCT } from '..';
import { errorHandler } from 'utils/ErrorHandler';

type ProductSubmitCallback = (productId: string) => void | Promise<void>;

export function useMutationProductItems() {
  const myInfo: MeData = useUserInformation((state: any) => state.userInfo);
  const params = useParams<{ id: string }>();

  const [createProduct, { loading: isCreateLoading }] =
    useMutation(CREATE_PRODUCT);
  const [saveProduct, { loading }] = useMutation(SAVE_PRODUCT);

  // 생성/저장 공용 잠금. 더블클릭 같은 연속 호출에서 두 번째 요청을 막고,
  // 콜백 체인(환불정책 조회 → 이동)이 끝날 때까지 유지한다.
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // 이 화면에서 이미 생성한 상품 ID. 생성 이후 다시 생성 요청이 오면 중복 생성 대신 해당 상품 저장으로 처리한다.
  const createdProductIdRef = useRef<string | null>(null);

  const lockSubmit = (): boolean => {
    if (isSubmittingRef.current || loading || isCreateLoading) {
      return false;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    return true;
  };

  const unlockSubmit = () => {
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };

  // iframe 스타일 즉시 적용
  const applyIframeStyles = (html: string): string => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const iframes = doc.querySelectorAll('iframe');

    iframes.forEach(iframe => {
      iframe.style.width = '100%';
      iframe.style.aspectRatio = '2 / 1';
      iframe.style.maxWidth = '100%';
    });

    return doc.body.innerHTML;
  };

  const handleSaveProduct = async (
    param: ProductParamInput,
    productId?: string,
    callback?: ProductSubmitCallback,
  ) => {
    if (!lockSubmit()) {
      return;
    }

    try {
      const htmlDetailContent = applyIframeStyles(param.htmlDetailContent);

      const response = await saveProduct({
        variables: {
          id: productId ? productId : params.id,
          param: { ...param, htmlDetailContent },
        },
      });

      if (response.data) {
        if (callback) {
          await callback(response.data.saveProduct.id);
        }
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      unlockSubmit();
    }
  };

  const handleCreateProduct = async (
    param: ProductParamInput,
    callback?: ProductSubmitCallback,
  ) => {
    if (createdProductIdRef.current) {
      await handleSaveProduct(param, createdProductIdRef.current, callback);

      return;
    }

    if (!lockSubmit()) {
      return;
    }

    try {
      const htmlDetailContent = applyIframeStyles(param.htmlDetailContent);

      const response = await createProduct({
        variables: {
          hostId: myInfo.me.host!.id,
          param: { ...param, htmlDetailContent },
        },
      });

      if (response) {
        const createdProductId: string = response.data.createProduct.id;

        createdProductIdRef.current = createdProductId;

        if (callback) {
          await callback(createdProductId);
        }
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      unlockSubmit();
    }
  };

  return {
    handleCreateProduct,
    saveProduct,
    handleSaveProduct,
    loading,
    isSubmitting,
  };
}
